import * as React from "react";
import {
    encodeOAuth2AuthErrorMessage,
    encodeOAuth2AuthTokenMessage,
    extractOAuth2AuthTokenFromCurrentURL,
} from "@glide/common-core/dist/js/authorization/auth";
import { exceptionToString } from "@glideapps/ts-necessities";
import { isUndefinedish, logError } from "@glide/support";
import { useRouteMatch } from "react-router-dom";
import { oauthRedirectPlay2Pattern, type OAuthRedirectPlay2RouteProps } from "@glide/common-core/dist/js/routes";

function getErrorMessageFromCurrentURL(): string | undefined {
    const { location } = window;
    const [hashKey, hashValue] = location.hash.substring(1).split("=").map(decodeURIComponent);
    return hashKey === "error" ? hashValue : undefined;
}

export const useOAuthLogin = () => {
    const [error, setError] = React.useState<string | undefined>();

    const match = useRouteMatch<OAuthRedirectPlay2RouteProps>(oauthRedirectPlay2Pattern);
    const appID = match?.params.id;

    React.useEffect(() => {
        const oauth2AuthToken = extractOAuth2AuthTokenFromCurrentURL();
        const opener = window.opener;

        if (oauth2AuthToken === undefined) {
            setError(getErrorMessageFromCurrentURL() ?? "Could not get OAuth token");
            return;
        }

        if (isUndefinedish(opener)) {
            // If there's no opener, it means that someone's signing in to the app directly from their IdP
            // (e.g. the Okta dashboard). We redirect them to the app auth screen which will know how to
            // process the token.

            // FIXME: We should be able to use React Router for redirections here, but it turns out that
            // this view is being served by the builder bundle, instead of the player's.
            window.location.href = `${getURLForApp(appID)}#signInWithOauth2Token=${oauth2AuthToken}`;
            return;
        }

        try {
            if (location.hash === "#signInWithOAuth2Error") {
                opener.postMessage(encodeOAuth2AuthErrorMessage(), location.origin);
                window.close();
                return;
            }

            opener.postMessage(encodeOAuth2AuthTokenMessage(oauth2AuthToken), location.origin);
            window.close();
            return;
        } catch (err: unknown) {
            logError(exceptionToString(err));
            setError("Could not post message to opener window");
            return;
        }
    }, [appID]);

    return { error };
};

function getURLForApp(appID: string | undefined) {
    // The appID won't actually be undefined, we need to type it like to match the type from the parser.
    // Also, we don't want to refine the type before because we only need the appID for running on localhost,
    // so let's not compromise staging or prod even if there's an unexpected error parsing the id.
    if (window.location.hostname === "localhost") {
        return `${window.location.protocol}//${window.location.host}/play/${appID}`;
    }
    return `${window.location.origin}`;
}
